import { ActivatedRouteSnapshot, ResolveFn, RouterStateSnapshot } from '@angular/router';
import { inject, Signal } from '@angular/core';
import { StaticDataService } from 'services/api/static-data.service';
import { Store } from '@ngrx/store';
import {
    selectAreas,
    selectBankData,
    selectMakesPageData,
} from 'stores/static-data/static-data.selectors';

import { StaticItem } from 'shared/base/generics/models/static-item';

type StaticDataSignals = {
    areas: Signal<{ areas?: StaticItem[] }>;
    makes: Signal<{ makes?: StaticItem[] }>;
    banks: Signal<{ isLoading: boolean; banks: StaticItem[]; commonBanks: StaticItem[] }>;
};

const getStaticDataSignals = (store: Store): StaticDataSignals => ({
    areas: store.selectSignal(selectAreas),
    makes: store.selectSignal(selectMakesPageData),
    banks: store.selectSignal(selectBankData),
});

const isDataLoaded = (data: StaticItem[] | undefined): boolean =>
    data !== undefined && data.length > 0;

const resolveHomeContents = (
    url: string,
    areas: Signal<{ areas?: StaticItem[] }>,
    staticDataService: StaticDataService,
): any => {
    if (url.includes('home-contents') && !isDataLoaded(areas().areas)) {
        return staticDataService.getHomeContents();
    }
    return null;
};

const resolveVehicle = (
    url: string,
    makes: Signal<{ makes?: StaticItem[] }>,
    staticDataService: StaticDataService,
): any => {
    if (url.includes('car-insurance') && !isDataLoaded(makes().makes)) {
        return staticDataService.getVehicle();
    }
    return null;
};

const resolvePolicy = (
    url: string,
    banks: Signal<{ isLoading: boolean; banks: StaticItem[]; commonBanks: StaticItem[] }>,
    staticDataService: StaticDataService,
): any => {
    if (shouldGetPolicyData(url, banks)) {
        return staticDataService.getPolicy();
    }
    return null;
};

const shouldGetPolicyData = (
    url: string,
    banks: Signal<{ isLoading: boolean; banks: StaticItem[]; commonBanks: StaticItem[] }>,
): boolean => {
    const isPolicyPath = url.includes('policy');
    const isBankDataLoaded = isDataLoaded(banks().banks);

    return isPolicyPath && !isBankDataLoaded;
};

export const staticDataResolver: ResolveFn<any> = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
) => {
    const store = inject(Store);
    const staticDataService = inject(StaticDataService);
    const signals = getStaticDataSignals(store);

    return (
        resolveHomeContents(state.url, signals.areas, staticDataService) ??
        resolveVehicle(state.url, signals.makes, staticDataService) ??
        resolvePolicy(state.url, signals.banks, staticDataService) ??
        null
    );
};
